import { IResolvers as ICoreResolvers, IClientService, IKeybindings, IKeybindingSource, CacheTypenames,
KeybindingContextMenuDocument, LifecyclePhase } from '@workbench-stack/core';
import { removeElementsAfterNulls } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/resolvedKeybindingItem';
import { reviveToResolvedKeybindingItem } from './revive-keyboard-input';

const getKeybindings = async (services) => {
    return new Promise((resolve) => {
        return services().lifecycleService.when(LifecyclePhase.Restored).then(() => {
            resolve(services().keybindingService.getKeybindings());
        });
    });
};

const getContextMenuData = async (context) => {
    let data = {
        keybindingContextMenu: {
            editItem: null,
            selectedItemIndex: null,
            editWhenIndex: null,
            newWhen: null,
            isOpenDialog: null,
            search: null,
            __typename: 'KeybindingContextMenu',
        },
    };
    try {
        data = await context.client.readQuery({
            query: KeybindingContextMenuDocument,
        });
    } catch (e) {}
    return data;
};

const setContextMenuData = async (context, data, newData) => {
    await context.client.writeQuery({
        query: KeybindingContextMenuDocument,
        data: {
            keybindingContextMenu: {
                ...data.keybindingContextMenu,
                ...newData,
            },
        },
    });
};


const keybindingsResolvers: (services: () => IClientService) => ICoreResolvers = (services) => {
    return ({
        Query: {
            getKeybindings: async (_, args, context) => {
                const keybindingItems: any = await getKeybindings(services);
                const keybindings = keybindingItems.map((keybinding, index) => ({
                        command: keybinding.command,
                        key: keybinding.keypressParts.join(' '),
                        when: keybinding.when ? keybinding.when.serialize() : '-',
                        source: keybinding.isDefault ? IKeybindingSource.Default : IKeybindingSource.User,
                        __typename: CacheTypenames.KeybindingItem,
                    }),
                );
                return { items: keybindings, __typename: CacheTypenames.Keybindings } as IKeybindings;
            },
        },
        Mutation: {
            registerKeybinding: (_, args, context) => {
                // services().keybindingService.resolveKeybinding(args.keybinding as any);
                return true;
            },
            editKeybinding: async (_, args, context) => {
                const kItem = reviveToResolvedKeybindingItem(args.keybindingItem);

                return services().keybindingEditingService.editKeybinding(kItem, args.key, args.when).then(() => {
                    return true;
                }).catch((e) => {
                    return false;
                });
            },
            removeKeybinding: async (_, args, context) => {
                const kItem = reviveToResolvedKeybindingItem(args.keybindingItem);

                return services().keybindingEditingService.removeKeybinding(kItem).then((res) => {
                    return true;
                }).catch((e) => {
                    return false;
                });
            },
            resetKeybinding: async (_, args, context) => {
                const kItem = reviveToResolvedKeybindingItem(args.keybindingItem);

                return services().keybindingEditingService.resetKeybinding(kItem).then((res) => {
                    return true;
                }).catch((e) => {
                    return false;
                });
            },
            resolveKeyboardEvent: async (_, args, context) => {
                const result = await services().keybindingService.resolveKeyboardEvent(args.keyboardEvent as any);
                const label = result.getLabel();
                const key = removeElementsAfterNulls(result.getDispatchParts());
                return { label, key };
            },
            lookupKeybinding: async (_, args, context) => {
                const result = await services().keybindingService.lookupKeybinding(args.commandId);
                return result ? result.getLabel() : '';
            },
            changeKeybinding: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    editItem: { ...args.editItem, __typename: 'EditKeybindingItem'},
                    selectedItemIndex: args.selectedIndex,
                    isOpenDialog: args.isOpenDialog,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
            changeWhenExpression: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    editItem: { ...args.editItem, __typename: 'EditKeybindingItem'},
                    editWhenIndex: args.editWhenIndex,
                    newWhen: args.newWhen,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
            searchKeybinding: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    search: args.value,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
            hideKeybindingDialog: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    isOpenDialog: false,
                    editItem: null,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
            stopEditWhenExpression: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    editItem: null,
                    editWhenIndex: -1,
                    newWhen: null,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
            setSelectedKeybindingIndex: async (_, args, context) => {
                const data = await getContextMenuData(context);
                const newData = {
                    selectedItemIndex: args.index,
                };
                await setContextMenuData(context, data, newData);
                return true;
            },
        },
    });
};

export { keybindingsResolvers };
